/**
 * <assurance-bar> — a row of assurances that becomes a one-at-a-time carousel on a
 * phone. Builds a dot per item, marks the one in view as the carousel is swiped, and
 * scrolls to an item when its dot is tapped. Desktop shows them all at once, so the
 * dots stay hidden by CSS and this only wires up the swipe indicator.
 */
(() => {
  class AssuranceBar extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-assurances-track]');
      this.dotsWrap = this.querySelector('[data-assurances-dots]');
      if (!this.track || !this.dotsWrap) return;

      // Items are wrapped in .shopify-block (dissolved with display:contents), so query
      // the assurances directly rather than the track's immediate children.
      this.items = [...this.track.querySelectorAll('.assurance')];
      if (this.items.length < 2) return;

      this.dotsWrap.replaceChildren();
      this.dots = this.items.map((item, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'assurances__dot';
        dot.setAttribute('aria-label', String(index + 1));
        dot.addEventListener('click', () => {
          this.track.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
        });
        this.dotsWrap.appendChild(dot);
        return dot;
      });

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.#mark(this.items.indexOf(entry.target));
          });
        },
        { root: this.track, threshold: 0.6 }
      );
      this.items.forEach((item) => this.observer.observe(item));
      this.#mark(0);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    #mark(index) {
      this.dots.forEach((dot, i) => {
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }
  }

  if (!customElements.get('assurance-bar')) {
    customElements.define('assurance-bar', AssuranceBar);
  }
})();
